'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridSolidFullProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridSolidFull - Clean grid pattern covering the full area
 *
 * Continuous 1px lines forming a square grid with no fade effect.
 * The pattern covers the entire container uniformly.
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridSolidFull />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridSolidFull({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridSolidFullProps = {}) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
